<template>
  <transition name="slide-down" mode="out-in">
    <div class="pt-12">
      <div class="flex justify-center px-6 mb-10"><Logo class="h-8" /></div>
      <div class="absolute left-0 right-0 flex justify-center items-center z-10 mt-0">
        <div class="border-grey-100 border-2 bg-gray-900 py-1 px-5 transform skew-x-20">
          <h2 class="text-white uppercase text-2xl font-medium transform -skew-x-20">
            Register For Orbital
          </h2>
        </div>
      </div>
      <div class="border border-gray-500 mx-auto mt-16 w-11/12 sm:w-6/12 lg:w-1/2
                  mb-12 px-12 py-10">
        <div class="flex flex-col mb-4 text-gray-200 text-sm">
          <p>
            Enter your organization details and the domains, IP addresses, or CIDR blocks
            you'd like to run Orbital against. Complete the form to see the applicable
            price before creating your account.
          </p>
          <ul class="list-disc pl-5 space-y-1">
            <strong>IMPORTANT:</strong>
            <li>
              Orgs with fewer than 5 hosts/IPs on the internet may not see great value here.
              Orbital is for larger environments.
            </li>
            <li>No free email accounts permitted (gmail/hotmail/tuta/etc).</li>
            <li>The email you register with must be from one of the domains you wish to scan.</li>
            <li>Orbital is for organizations/companies, not one-off personal websites.</li>
            <li>Orbital is not a "vuln scanner" and won’t dump pages of CVEs or MITRE stuff.</li>
            <li>
              The purpose is to highlight Attack Pathways(tm) and entry points, many of which
              may never get a CVE assigned.
            </li>
          </ul>
        </div>

        <div class="mb-2">
          <TextInput id="org-name" type="text" label="Organization Name"
                     placeholder="Organization name..." v-model="name"
                     :errors="fieldErrors.name" />
        </div>
        <div class="mb-8">
          <Textarea id="whitelist" type="text"
                    label="Requested domain(s), IP address(es), or CIDR block(s)"
                    placeholder="Enter comma-separated targets to run Orbital reports against"
                    v-model="whitelist" :errors="fieldErrors.whitelist" />
          <p class="text-sm">Enter every authorized target you want included in the quote.</p>
          <p class="italic text-gray-300">Example: test.com, test.co.uk, 8.8.8.0/24</p>
        </div>
        <div class="mb-2 grid grid-cols-2 gap-4">
          <TextInput id="first-name" type="text" label="First Name"
                     placeholder="First name..." v-model="adminFirstName"
                     :errors="fieldErrors.adminFirstName" />
          <TextInput id="last-name" type="text" label="Last Name"
                     placeholder="Last name..." v-model="adminLastName"
                     :errors="fieldErrors.adminLastName" />
        </div>
        <div class="mb-2">
          <TextInput id="email" type="email" label="Email" placeholder="Email address..."
                     v-model="adminEmail" :errors="fieldErrors.adminEmail" />
        </div>
        <div class="mb-5">
          <TextInput id="password" type="password" label="Password"
                     placeholder="Create a password..." v-model="adminPassword"
                     :errors="fieldErrors.adminPassword" />
        </div>
        <label class="flex items-start text-sm text-gray-200 mb-5">
          <input class="mt-1 mr-3" type="checkbox" v-model="authorized" />
          <span>I confirm that I am authorized to assess every target entered above.</span>
        </label>
        <vue-hcaptcha v-if="!isLocal" @verify="onVerifyCaptcha" :sitekey="H_CAPTCH_KEY"
                      theme="dark" />

        <div class="flex flex-wrap items-center mt-4">
          <Button :disabled="quoteDisabled" :inFlight="quoteLoading" type="primary"
                  @onClick="handleQuote">Submit</Button>
          <span class="text-xs text-gray-300 ml-4">Usually takes less than a minute.</span>
        </div>

        <div v-if="quoteLoading" class="quote-panel text-center">
          <span class="quote-spinner"></span>
          <strong class="uppercase tracking-wider">Checking your submission...</strong>
          <p class="text-sm text-gray-300 mt-2 mb-5">Getting you the best price!</p>
        </div>

        <div v-if="quoteEmailPending" class="quote-success">
          <strong>Your quote request has been received</strong>
          We are handling more quote requests than usual. Your quote will be emailed to
          {{ adminEmail }} when it is ready. You can close this page.
        </div>

        <div v-if="quote && quote.status === 'complete'" class="quote-panel">
          <div class="grid grid-cols-1 sm:grid-cols-2 border-b border-gray-700">
            <div class="quote-stat">
              <div class="quote-label">Estimated public hosts</div>
              <div class="quote-value">{{ quote.hostCount.toLocaleString() }}</div>
            </div>
            <div class="quote-stat sm:border-l border-gray-700">
              <div class="quote-label">Price</div>
              <div class="quote-value">${{ quote.monthlyPrice.toLocaleString() }}
                <small>/ month</small>
              </div>
            </div>
          </div>
          <div class="p-5">
            <div class="quote-label text-orange-500">Your estimated plan</div>
            <div class="text-2xl my-1">{{ quotePlanName }}</div>
            <p class="text-sm text-gray-300">
              This estimate uses currently observable public infrastructure. Your confirmed
              host-based tier will use the inventory found during your first report.
            </p>
            <div class="flex flex-wrap gap-3 mt-5">
              <Button type="primary" :inFlight="isLoading" @onClick="subscribeNow">
                Subscribe now
              </Button>
              <Button type="secondary-orange" :disabled="isLoading" @onClick="requestTrial">
                Request a free trial
              </Button>
            </div>
          </div>
        </div>

        <div v-if="quoteError" class="quote-error">{{ quoteError }}</div>
        <div v-if="invoiceRequested" class="quote-success">
          <strong>Invoice requested</strong>
          Your account has been created. Square is preparing the invoice and will also send it
          to the email address you provided.
        </div>
        <div v-if="globalErrors.length" class="mt-8">
          <ErrorMessage v-for="(error, index) in globalErrors" :key="`error${index}`"
                        :error="error" />
        </div>
      </div>

      <div class="border border-gray-500 mx-auto w-11/12 sm:w-6/12 lg:w-1/2
                  mb-12 px-6 py-3 text-center">
        <p class="text-sm">Already have an account?</p>
        <router-link :to="{ name: 'login' }"
          class="text-gray-200 text-sm uppercase outline-none hover:text-white">
          Login
        </router-link>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { constants } from '@/config';
import Button from '@/components/Button.vue';
import Logo from '@/assets/orbital_logo.svg?inline';
import TextInput from '@/components/TextInput.vue';
import Textarea from '@/components/Textarea.vue';
import ErrorMessage from '@/components/ErrorMessage.vue';
import VueHcaptcha from '@hcaptcha/vue-hcaptcha';

const { H_CAPTCH_KEY } = constants;

const ERROR_FIELD_LABELS = {
  name: 'Organization name',
  whitelist: 'Requested targets',
  adminFirstName: 'First name',
  adminLastName: 'Last name',
  adminEmail: 'Email',
  adminPassword: 'Password',
  quoteId: 'Quote',
};

function errorValues(value) {
  if (Array.isArray(value)) {
    return value.reduce((messages, item) => messages.concat(errorValues(item)), []);
  }
  if (value && typeof value === 'object') {
    return Object.keys(value).reduce(
      (messages, key) => messages.concat(errorValues(value[key])),
      [],
    );
  }
  if (value === null || value === undefined || value === '') return [];
  return [String(value)];
}

export function quoteResponseError(error) {
  const response = error && error.response;
  const data = response && response.data;
  if (!data || typeof data !== 'object') return null;

  const messages = [];
  Object.keys(data).forEach((fieldName) => {
    if (fieldName === 'rawResponseData') return;
    const label = ERROR_FIELD_LABELS[fieldName];
    errorValues(data[fieldName]).forEach((message) => {
      messages.push(label ? `${label}: ${message}` : message);
    });
  });
  return Array.from(new Set(messages)).join(' ');
}

export default {
  name: 'Register',
  components: {
    Button, ErrorMessage, Logo, Textarea, TextInput, VueHcaptcha,
  },
  data() {
    return {
      whitelist: null,
      name: null,
      adminFirstName: null,
      adminLastName: null,
      adminEmail: null,
      adminPassword: null,
      authorized: false,
      captchaIncompleteOrFailed: window.location.hostname !== 'localhost',
      isLocal: window.location.hostname === 'localhost',
      H_CAPTCH_KEY,
      quoteLoading: false,
      quote: null,
      quoteError: null,
      quotePoll: null,
      quoteEmailPending: false,
      invoiceRequested: false,
    };
  },
  computed: {
    ...mapGetters(['isLoading', 'globalErrors', 'fieldErrors']),
    incompleteForm() {
      return !this.name || !this.whitelist || !this.adminFirstName
        || !this.adminLastName || !this.adminEmail || !this.adminPassword;
    },
    quoteDisabled() {
      return this.incompleteForm || !this.authorized || this.captchaIncompleteOrFailed;
    },
    quotePlanName() {
      const hosts = this.quote.hostCount;
      if (hosts <= 100) return '1–100 public hosts';
      if (hosts <= 499) return '101–499 public hosts';
      if (hosts <= 999) return '500–999 public hosts';
      return '1,000+ public hosts';
    },
  },
  beforeMount() {
    this.logout();
  },
  beforeDestroy() {
    clearTimeout(this.quotePoll);
  },
  created() {
    this.clearGlobalErrors();
    this.clearFieldErrors();
  },
  methods: {
    ...mapActions([
      'clearGlobalErrors', 'clearFieldErrors', 'logout', 'login',
      'requestSignupQuote', 'fetchSignupQuote', 'createOrganization',
    ]),
    onVerifyCaptcha() {
      this.captchaIncompleteOrFailed = false;
    },
    targets() {
      return this.whitelist.split(/[\s,]+/).filter(Boolean);
    },
    quoteRequest() {
      return {
        name: this.name,
        whitelist: this.targets(),
        adminFirstName: this.adminFirstName,
        adminLastName: this.adminLastName,
        adminEmail: this.adminEmail,
      };
    },
    signupRequest(subscribeNow) {
      const targets = this.targets();
      return {
        ...this.quoteRequest(),
        domain: targets[0],
        adminPassword: this.adminPassword,
        quoteId: this.quote.quoteId,
        subscribeNow,
      };
    },
    async handleQuote() {
      if (this.quoteDisabled) return;
      clearTimeout(this.quotePoll);
      this.clearGlobalErrors();
      this.clearFieldErrors();
      this.quote = null;
      this.quoteError = null;
      this.quoteEmailPending = false;
      this.invoiceRequested = false;
      this.quoteLoading = true;
      try {
        const quote = await this.requestSignupQuote(this.quoteRequest());
        await this.pollQuote(quote.quoteId, 0);
      } catch (error) {
        this.quoteLoading = false;
        this.quoteError = quoteResponseError(error)
          || 'The quote could not be started. Please check the form and try again.';
      }
    },
    async pollQuote(quoteId, attempts) {
      try {
        const quote = await this.fetchSignupQuote(quoteId);
        if (quote.status === 'complete') {
          this.quote = quote;
          this.quoteLoading = false;
          return;
        }
        if (quote.status === 'failed' || attempts >= 60) {
          this.quoteLoading = false;
          this.quoteError = quote.error || 'The quote could not be completed. Please try again.';
          return;
        }
        if (quote.deliveryMode === 'email') {
          this.quoteLoading = false;
          this.quoteEmailPending = true;
          return;
        }
        this.quotePoll = setTimeout(() => this.pollQuote(quoteId, attempts + 1), 2000);
      } catch (error) {
        this.quoteLoading = false;
        this.quoteError = quoteResponseError(error)
          || 'The quote could not be completed. Please try again.';
      }
    },
    openInvoiceWindow() {
      const popup = window.open('', '_blank');
      if (popup) {
        popup.document.title = 'Orbital invoice';
        popup.document.body.innerText = 'Creating your Orbital invoice. Please wait...';
      }
      return popup;
    },
    async subscribeNow() {
      const invoiceWindow = this.openInvoiceWindow();
      this.invoiceRequested = true;
      try {
        const response = await this.createOrganization(this.signupRequest(true));
        if (response.subscriptionUrl) {
          if (invoiceWindow) invoiceWindow.location.replace(response.subscriptionUrl);
          else window.open(response.subscriptionUrl, '_blank');
        } else if (invoiceWindow) invoiceWindow.close();
        if (response.subscriptionError) this.quoteError = response.subscriptionError;
        await this.login({ email: this.adminEmail, password: this.adminPassword });
      } catch (error) {
        if (invoiceWindow) invoiceWindow.close();
        this.invoiceRequested = false;
      }
    },
    async requestTrial() {
      try {
        await this.createOrganization(this.signupRequest(false));
        await this.login({ email: this.adminEmail, password: this.adminPassword });
      } catch (error) {
        // Store actions surface the field and global errors.
      }
    },
  },
};
</script>

<style scoped>
.quote-panel { @apply mt-6 border border-gray-700 bg-gray-900; }
.quote-stat { @apply p-5; }
.quote-label { @apply uppercase tracking-widest text-xs text-gray-300; }
.quote-value { @apply text-3xl mt-1; }
.quote-value small { @apply text-sm text-gray-300; }
.quote-spinner {
  display: inline-block; width: 22px; height: 22px; margin: 22px 10px 0 0;
  border: 2px dotted #f04420; border-radius: 50%; vertical-align: middle;
  animation: spin 1s linear infinite;
}
.quote-error { @apply mt-6 p-4 border-l-4 border-red-500 bg-gray-900 text-sm; }
.quote-success { @apply mt-6 p-4 border-l-4 border-green-500 bg-gray-900 text-sm; }
.quote-success strong { @apply block uppercase tracking-widest text-xs text-green-500 mb-1; }
@keyframes spin { to { transform: rotate(360deg); } }
.slide-down-enter-active, slide-down-leave-active { transition: all .2s ease-in-out; }
.slide-down-enter, .slide-down-leave-to { transform: translateY(-10px); opacity: 0; }
</style>
